import { store } from "@app/store"
import { selectAccessToken } from "@features/login/loginSelectors"
import {
  Configuration,
  ProgramDomain,
  TrackApi,
  TrackDto,
  TrackMetadataDto,
} from "@masterschool/course-builder-api"

export type CreateTrackBody = {
  internalName: string
  externalName?: string
  domain: ProgramDomain
  durationInMonths: number
}

export class TrackClient {
  private static trackClient: TrackApi

  // POST /v1/track. Called via fetch because the generated client predates this
  // endpoint (regenerating @masterschool/course-builder-api is a separate step).
  static async createTrack(body: CreateTrackBody): Promise<TrackDto> {
    const basePath = `${import.meta.env.VITE_BASE_URL ?? ""}/api`
    const response = await fetch(`${basePath}/v1/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${selectAccessToken(store.getState())}`,
      },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      throw new Error(
        `Failed to create track (${response.status} ${response.statusText})`,
      )
    }
    return response.json()
  }

  private static getTrackClient() {
    if (!this.trackClient) {
      this.trackClient = new TrackApi(
        new Configuration({
          basePath: `${import.meta.env.VITE_BASE_URL ?? ""}/api`,
        }),
      )
    }
    return this.trackClient
  }

  private static makeHeaders() {
    return {
      headers: {
        Authorization: `Bearer ${selectAccessToken(store.getState())}`,
      },
    }
  }

  static getTrack(id: string, version?: number) {
    return TrackClient.getTrackClient()
      .trackControllerV1Get(id, version, TrackClient.makeHeaders())
      .then((response) => response.data)
  }

  static list() {
    return TrackClient.getTrackClient()
      .trackControllerV1List(TrackClient.makeHeaders())
      .then((response) => {
        return response.data
      })
  }

  static save(trackId: string, track: TrackDto) {
    return TrackClient.getTrackClient()
      .trackControllerV1Update(track, trackId, TrackClient.makeHeaders())
      .then((response) => response.data)
  }

  static async updateSyllabusMetadata(
    request: TrackMetadataDto,
    id: string,
  ): Promise<TrackMetadataDto> {
    return await TrackClient.trackClient
      .trackControllerV1UpdateTrackMetadata(
        request,
        id,
        TrackClient.makeHeaders(),
      )
      .then((response) => {
        return response.data
      })
  }

  static publish(trackId: string) {
    return TrackClient.getTrackClient()
      .trackControllerV1Publish(trackId, TrackClient.makeHeaders())
      .then((response) => response.data)
  }

  static listVersions(id: string) {
    return TrackClient.getTrackClient()
      .trackControllerV1ListTrackVersions(id, TrackClient.makeHeaders())
      .then((response) => response.data)
  }

  static createNewDraft(id: string) {
    return TrackClient.getTrackClient().trackControllerV1CreateNewDraftVersion(
      id,
      TrackClient.makeHeaders(),
    )
  }
}
